import { faCaretDown, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { List } from "react-virtualized-compat";
import { useMouseOutside } from "../../utils";
import { debounce } from "../../utils/debounce";
import { Container, Filter, NoResults, Options, OptionsContainer } from "./styled";
import { SelectOption, SelectWithFilterProps, SelectWithFilterValue } from "./types";

let remoteFilterDebouncer: number;

export const SelectWithFilter: React.FC<SelectWithFilterProps> = ({
  itemName,
  selectedValue,
  options,
  isLoading,
  onFilterChange,
  onChange,
  placeholder,
  maxResults = 1000,
  returnValueKey = "id",
  readOnly = false,
  rowHeight = 25,
  maxRows = 5,
  background,
  isRelative,
}: SelectWithFilterProps) => {
  const listboxId = useId();
  const optionsContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionsRef = useRef<Array<HTMLDivElement | null>>([]);

  const [isTyping, setIsTyping] = useState(false);
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options ?? []);
  const [showOptions, setShowOptions] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const [initialValue, setInitialValue] = useState(selectedValue);

  const getOption = useCallback(
    (value: SelectWithFilterValue) =>
      value ? filteredOptions.find(({ id, label }) => [id, label].includes(value)) : null,
    [filteredOptions]
  );
  const selectedOptionLabel = getOption(selectedValue)?.label ?? "";

  // Get or generate a placeholder for the input
  const getPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }

    if (!onFilterChange) {
      return "Choose from list...";
    }

    if (itemName) {
      return `Choose a ${itemName} or type to filter...`;
    }

    return "Choose an item or type to filter...";
  };

  // Handler container onFocus actions
  const handleContainerFocus = () => {
    const selected = getOption(selectedValue);
    if (selected && !filterValue && onFilterChange) {
      setFilteredOptions([selected]);
    }

    const initial = getOption(initialValue);
    if (initial && !selected) {
      setFilteredOptions([initial]);
    }

    setShowOptions(true);
  };

  // Handle container onBlur actions
  const handleContainerBlur = useCallback(() => {
    if (showOptions) {
      setIsTyping(false);
      setShowOptions(false);
      setFocusedOptionIndex(-1);
      inputRef?.current?.blur();
      optionsContainerRef?.current?.blur();

      if (filterValue) {
        setFilterValue("");
        setLastQuery("");
        onFilterChange?.("");
      }
    }
  }, [showOptions, filterValue]);
  useMouseOutside(optionsContainerRef, handleContainerBlur);

  // Handle options keyboard control
  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
      handleContainerBlur();
    } else if (e.keyCode === 9) {
      handleContainerBlur();
    } else if (e.keyCode === 38 || e.keyCode === 40) {
      e.preventDefault();
      e.stopPropagation();
      setShowOptions(true);

      let newIndex = e.keyCode === 38 ? focusedOptionIndex - 1 : focusedOptionIndex + 1;
      const optionsLength = filteredOptions.length;

      if (newIndex < 0) {
        newIndex = -1;
        inputRef?.current?.focus();
      } else if (newIndex > optionsLength - 1) {
        newIndex = optionsLength - 1;
      }

      setFocusedOptionIndex(newIndex);
      const option = optionsRef?.current?.[newIndex] as HTMLDivElement;
      option?.focus();
    } else {
      inputRef?.current?.focus();
    }
  };

  // Set selected option and hide options
  const handleOptionSelection = useCallback(
    (option: SelectOption | null) => {
      onChange(returnValueKey === "label" ? option?.label : option?.id ?? null);
      handleContainerBlur();
    },
    [onChange, returnValueKey, handleContainerBlur]
  );

  // Handle input behavior when pressing 'enter'
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();

      if (showOptions) {
        if (filteredOptions.length) {
          handleOptionSelection(filteredOptions[0]);
        }
      }
    }
  };

  // Set filter value and filter options remotely (if needed) as user types
  const handleFilterChange = (currentFilter: string) => {
    if (!currentFilter || currentFilter !== selectedOptionLabel) {
      setIsTyping(true);
      setFilterValue(currentFilter);
      onChange?.(null);
      remoteFilterDebouncer = debounce(
        () =>
          (() => {
            setIsTyping(false);
            if (
              onFilterChange &&
              (currentFilter.endsWith(" ") || !lastQuery || currentFilter !== lastQuery)
            ) {
              setLastQuery(currentFilter);
              onFilterChange(currentFilter);
            }
          })(),
        remoteFilterDebouncer,
        600
      );
    }
  };

  // Filter options locally as user types
  useEffect(() => {
    if (showOptions && (!onFilterChange || !selectedValue)) {
      setFilteredOptions(
        filterValue
          ? options.filter((option) =>
            option.label.toLowerCase().includes(filterValue.toLowerCase())
          )
          : options
      );
    }
  }, [options, filterValue, selectedValue, onFilterChange, showOptions]);

  // Reset options and value if needed
  useEffect(() => {
    if (itemName) {
      setFilteredOptions([]);
      onChange(null);
      setFilterValue("");
      setLastQuery("");
      onFilterChange?.("");
    }
  }, [itemName, onChange, onFilterChange]);

  // Set up initial value
  useEffect(() => {
    if (initialValue && !isLoading && options.length) {
      const initialOption = options.find(({ id, label }) => [id, label].includes(initialValue));
      if (initialOption) {
        setFilteredOptions([initialOption]);
        handleOptionSelection(initialOption);
        setInitialValue(null);

        setLastQuery("");
        onFilterChange?.("");
      } else if (!lastQuery) {
        setLastQuery(initialValue.toString());
        onFilterChange?.(initialValue.toString());
      }
    }
  }, [initialValue, options]);

  return readOnly ? (
    <div>
      {selectedOptionLabel ||
        (typeof initialValue === "string" && initialValue) ||
        (typeof selectedValue === "string" && selectedValue) ||
        "-"}
    </div>
  ) : (
    <Container
      data-testid="select-with-filter"
      tabIndex={onFilterChange ? undefined : 0}
      onClick={handleContainerFocus}
      onKeyDown={handleContainerKeyDown}
      ref={optionsContainerRef}
    >
      <Filter showOptions={showOptions} background={background}>
        <input
          id={placeholder}
          name={placeholder}
          type="text"
          role="combobox"
          aria-autocomplete={onFilterChange ? "list" : "none"}
          aria-controls={listboxId}
          aria-expanded={showOptions}
          aria-haspopup="listbox"
          aria-activedescendant={
            focusedOptionIndex >= 0 ? `${listboxId}-option-${focusedOptionIndex}` : undefined
          }
          autoComplete="off"
          placeholder={getPlaceholder()}
          onChange={({ target }) => handleFilterChange(target.value)}
          onKeyDown={handleInputKeyDown}
          value={getOption(initialValue)?.label || filterValue || selectedOptionLabel}
          readOnly={!onFilterChange}
          ref={inputRef}
        />
        <FontAwesomeIcon
          icon={showOptions && (isLoading || isTyping) ? faCircleNotch : faCaretDown}
          spin={showOptions && (isLoading || isTyping)}
          size="1x"
        />
      </Filter>
      {showOptions ? (
        <OptionsContainer
          isRelative={isRelative}
          data-testid="select-with-filter-options"
          hasOptions={!!filteredOptions.length}
        >
          {filteredOptions.length ? (
            <Options id={listboxId} role="listbox">
              <List
                width={1}
                height={
                  filteredOptions.length * rowHeight > rowHeight * maxRows
                    ? rowHeight * maxRows
                    : filteredOptions.length * rowHeight
                }
                containerStyle={{
                  width: "100%",
                  maxWidth: "100%",
                }}
                style={{
                  width: "100%",
                }}
                rowCount={filteredOptions.length}
                rowHeight={rowHeight}
                scrollTop={isTyping ? 0 : undefined}
                scrollToIndex={
                  filteredOptions.findIndex(({ label }) => label === selectedOptionLabel) + 2
                }
                rowRenderer={({ index, style }: { index: number; style: React.CSSProperties }) => {
                  const option = filteredOptions[index];
                  const highlightValue = filterValue || selectedOptionLabel;
                  let optionHighlighted;

                  if (highlightValue) {
                    if (!onFilterChange) {
                      if (option.label === highlightValue) {
                        optionHighlighted = <strong>{option.label}</strong>;
                      }
                    } else {
                      const regexp = new RegExp(
                        `(${highlightValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                        "gi"
                      );
                      const parts = option.label.split(regexp);
                      optionHighlighted = parts.map((part) =>
                        regexp.test(part) ? (
                          <strong key={`${part}${Math.random()}`}>{part}</strong>
                        ) : (
                          part
                        )
                      );
                    }
                  }

                  return (
                    <div
                      id={`${listboxId}-option-${index}`}
                      key={option.label}
                      className="option"
                      role="option"
                      aria-selected={focusedOptionIndex === index}
                      tabIndex={index}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionSelection(option);
                        setShowOptions(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOptionSelection(option);
                        }
                      }}
                      style={{
                        ...style,
                        width: "calc(100% - 32px)",
                        height: "15px",
                      }}
                      ref={(el) => {
                        optionsRef.current[index] = el;
                      }}
                    >
                      {optionHighlighted ?? option.label}
                    </div>
                  );
                }}
              />
            </Options>
          ) : (
            <NoResults>
              {isTyping || isLoading || (lastQuery === filterValue && filteredOptions.length)
                ? "Loading..."
                : `No ${itemName ? `${itemName}s` : "items"} found`}
            </NoResults>
          )}
        </OptionsContainer>
      ) : null}
    </Container>
  );
};
